import {
    faBell,
    faChevronDown,
    faCircle,
    faExternalLinkAlt,
    faMemoPad,
} from '@fortawesome/pro-solid-svg-icons';
import { IAssetAlertSummaryResponse } from '@kcftech/prometheus-client';
import { GroupHierarchyText } from 'components/group_hierarchy/GroupHierarchyText';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { ChildAlert } from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/ChildAlert';
import {
    AlertContainer,
    AlertContent,
    AlertCount,
    AlertInfo,
    AlertInfoWrapper,
    AssetName,
    ChevronIcon,
    Description,
    ExpandButton,
    ExpandedContent,
    ExpanderRow,
    ExternalLinkIcon,
    HeadingContainer,
    IconButton,
    IconsContainer,
    MemoPadIcon,
    NotificationBell,
    NotificationIcon,
    OverlayLink,
    SeverityDot,
    SeverityLozenge,
    Timestamp,
    TooltipArea,
    TopRow,
} from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/styled';
import {
    formatAlertTimestamp,
    getIndicatorHierarchyFromAlert,
    getWorkbenchUrl,
} from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/utils';
import { useRef } from 'react';
import {
    getAlertSeverity,
    getStandardizedSeverity,
    SEVERITY_LABEL_MAP,
} from 'utils/alert_severity';
import { getIssueDetailsRoute } from 'utils/RouteHelper';

export type AlertItemProps = {
    alert: IAssetAlertSummaryResponse;
    depth?: number;
    locationId: string;
    expanded: boolean;
    onToggle: () => void;
};

export function AlertItem({
    alert,
    depth = 0,
    locationId,
    expanded,
    onToggle,
}: AlertItemProps) {
    const { formatDate, formatTime } = useUserDateTimeFormat(false);
    const linkRef = useRef<HTMLAnchorElement>(null);

    if (!alert.Alerts.length) {
        return null;
    }

    const mainAlert = alert.Alerts[0];
    const alertCount = alert.Alerts.length;
    const hasChildren = alertCount > 1;
    const childAlerts = alert.Alerts.slice(1);
    const indicator = getIndicatorHierarchyFromAlert(mainAlert);

    const firstIssueId = alert.Alerts.find((alert) => alert.IssueId)?.IssueId;

    function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
        }
    }

    const handleTooltipClick = (event: React.MouseEvent) => {
        // Stop the event from propagating to prevent double navigation
        event.stopPropagation();
        // Programmatically click the underlying link
        linkRef.current?.click();
    };

    const mainAlertSeverity = getAlertSeverity(mainAlert.AlertType);
    const standardSeverity = getStandardizedSeverity(mainAlertSeverity);
    const severityLabel = SEVERITY_LABEL_MAP[standardSeverity];

    return (
        <AlertContainer $depth={depth}>
            <AlertContent>
                <TopRow>
                    <HeadingContainer>
                        <AssetName>
                            <TooltipArea>
                                <GroupHierarchyText
                                    hierarchy={alert.Hierarchy}
                                    suffix={alert.Name}
                                />
                            </TooltipArea>
                        </AssetName>
                    </HeadingContainer>
                    <IconsContainer>
                        {firstIssueId && (
                            <IconButton
                                href={getIssueDetailsRoute(locationId, firstIssueId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open issue in new tab"
                            >
                                <MemoPadIcon icon={faMemoPad} aria-hidden="true" />
                            </IconButton>
                        )}
                        <NotificationIcon>
                            <NotificationBell icon={faBell} aria-hidden="true" />
                            <AlertCount>{alertCount}</AlertCount>
                        </NotificationIcon>
                    </IconsContainer>
                </TopRow>
                <AlertInfoWrapper>
                    <OverlayLink
                        ref={linkRef}
                        to={getWorkbenchUrl(indicator.hierarchy, mainAlert.AlertId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View trend for ${indicator.name}`}
                    />
                    <AlertInfo>
                        <SeverityLozenge $severity={mainAlertSeverity}>
                            {severityLabel}
                        </SeverityLozenge>
                        <Description>
                            <TooltipArea
                                className="description-tooltip-area"
                                onClick={handleTooltipClick}
                            >
                                <GroupHierarchyText
                                    suffixClassName="alert-item-indicator-text"
                                    hierarchy={indicator.hierarchy}
                                    suffix={indicator.name}
                                />
                            </TooltipArea>
                            <ExternalLinkIcon icon={faExternalLinkAlt} />
                        </Description>
                    </AlertInfo>
                    <Timestamp>
                        {formatAlertTimestamp(
                            formatDate,
                            formatTime,
                            new Date(mainAlert.AlertTime),
                        )}
                    </Timestamp>
                </AlertInfoWrapper>
                {hasChildren && (
                    <ExpanderRow>
                        <ExpandButton
                            onClick={onToggle}
                            onKeyDown={handleKeyDown}
                            $expanded={expanded}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${
                                childAlerts.length
                            } child alerts for ${alert.Name}`}
                        >
                            <SeverityDot
                                icon={faCircle}
                                aria-hidden="true"
                                $severity={getAlertSeverity(childAlerts[0].AlertType)}
                            />
                            +{childAlerts.length}
                            <ChevronIcon
                                icon={faChevronDown}
                                aria-hidden="true"
                                $expanded={expanded}
                            />
                        </ExpandButton>
                        {expanded && (
                            <ExpandedContent>
                                {childAlerts.map((alert) => (
                                    <ChildAlert
                                        key={alert.AlertId}
                                        alert={alert}
                                        formatDate={formatDate}
                                        formatTime={formatTime}
                                    />
                                ))}
                            </ExpandedContent>
                        )}
                    </ExpanderRow>
                )}
            </AlertContent>
        </AlertContainer>
    );
}
